import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import OptimizedImage from "@/components/OptimizedImage";
import BookLookInsideModal from "@/components/BookLookInsideModal";
import Reveal from "@/components/Reveal";

export default function IdentityShiftPage() {
  const [lookInsideBook, setLookInsideBook] = useState(null);
  return (
    <Layout>
      {/* Hero — Break the Pattern. Become the Future. */}
      <header className="hero">
        <div className="container hero-grid">
          <div>
            <h1 className="hero-title">
              THE IDENTITY SHIFT™ <br />
              <span className="text-gold">BREAK THE PATTERN. BECOME THE FUTURE.</span>
            </h1>
            <div style={{ fontSize: "1rem", color: "var(--gold-warm)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "16px" }}>
              Science. Mindset. Neuroscience. Transformation.
            </div>
            <p className="hero-subtitle">
              Your life is shaped by patterns you learned, beliefs you inherited, identities you reinforced, and choices you repeat. The Identity Shift is a collection of books and practical tools designed to help you understand those patterns, rewire your identity, and consciously build what comes next.
            </p>
            <div className="hero-actions">
              <a href="#offer" className="btn btn-primary-gold btn-lg">
                GET THE COMPLETE SYSTEM ($47) →
              </a>
            </div>
            <div className="hero-trust">
              <span className="hero-trust__chip"><strong>8</strong> products</span>
              <span className="hero-trust__chip"><strong>900+</strong> pages</span>
              <span className="hero-trust__chip">Instant digital download</span>
            </div>
          </div>

          <div style={{ textAlign: "center" }}>
            <OptimizedImage
              src="/assets/author/identity-shift-poster.jpg"
              alt="The Identity Shift by Rajesh Sivacharya — 3 ebooks and 4 journals. Break the pattern. Become the future."
              width={853}
              height={1280}
              priority
              style={{ width: "100%", maxWidth: "460px", height: "auto", borderRadius: "var(--radius-xl)", border: "1px solid var(--border-gold)", boxShadow: "0 20px 50px rgba(0,0,0,0.6)" }}
            />
          </div>
        </div>
      </header>
      {/* The Identity Shift™ Framework — 4-pillar transformation system */}
      <Reveal as="section" className="container section-padding">
        <div style={{ maxWidth: "840px", margin: "0 auto", textAlign: "center" }}>
          <div className="hero-kicker">THE TRANSFORMATION SYSTEM</div>
          <h2 style={{ fontSize: "3rem", marginTop: "8px" }}>THE IDENTITY SHIFT™ FRAMEWORK</h2>
          <p style={{ color: "var(--text-secondary)", fontSize: "1.1rem", lineHeight: 1.8, marginTop: "16px" }}>
            A four-pillar visual architecture for understanding patterns, rewiring identity, and creating intentional change.
          </p>
        </div>

        <div className="framework-diagram">
          <div className="framework-steps">
            <div className="framework-step">
              <div className="step-num">01</div>
              <div className="step-title">UNDERSTAND</div>
              <div className="step-desc">Discover the patterns shaping your life.</div>
            </div>
            <div className="framework-step">
              <div className="step-num">02</div>
              <div className="step-title">REWIRE</div>
              <div className="step-desc">Change the beliefs and identity beneath those patterns.</div>
            </div>
            <div className="framework-step">
              <div className="step-num">03</div>
              <div className="step-title">ALIGN</div>
              <div className="step-desc">Bring your actions into alignment with who you are becoming.</div>
            </div>
            <div className="framework-step">
              <div className="step-num">04</div>
              <div className="step-title">CREATE</div>
              <div className="step-desc">Design your future intentionally.</div>
            </div>
          </div>
        </div>
      </Reveal>

      {/* The Trilogy — three books (BookCard grid) */}
      <Reveal as="section" className="container section-padding" style={{ borderTop: "1px solid var(--border-glass)" }}>
        <div style={{ textAlign: "center", maxWidth: "800px", margin: "0 auto 48px auto" }}>
          <div className="hero-kicker">THE TRILOGY</div>
          <h2 style={{ fontSize: "2.8rem" }}>THREE BOOKS. ONE TRANSFORMATION JOURNEY.</h2>
          <p style={{ color: "var(--text-secondary)", fontSize: "1.05rem", marginTop: "8px" }}>
            Start by understanding the pattern. Explore the possibilities beyond it. Then discover what you inherited—and what ends with you.
          </p>
        </div>

        <div className="grid-3">
          {BOOKS.map((book) => (
            <BookCard key={book.num} {...book} onLookInside={() => setLookInsideBook(book.id)} />
          ))}
        </div>
      </Reveal>

      {/* 30-Day Guided Journals — Wealth, Success, Health, Relationships (JournalCard grid) */}
      <Reveal as="section" className="container section-padding">
        <div style={{ textAlign: "center", maxWidth: "800px", margin: "0 auto 48px auto" }}>
          <div className="hero-kicker">30-DAY GUIDED PRACTICES</div>
          <h2 style={{ fontSize: "2.8rem" }}>TURN INSIGHT INTO PRACTICE</h2>
          <p style={{ color: "var(--text-secondary)", fontSize: "1.1rem", marginTop: "8px" }}>
            The books help you understand. The journals help you practice.
          </p>
        </div>

        <div className="grid-4">
          {JOURNALS.map((journal) => (
            <JournalCard key={journal.type} {...journal} onLookInside={() => setLookInsideBook(journal.id)} />
          ))}
        </div>
      </Reveal>

      {/* Daily Practice Kit — everyday reinforcement feature */}
      <Reveal as="section" className="container section-padding" style={{ borderTop: "1px solid var(--border-glass)" }}>
        <div style={{ background: "linear-gradient(135deg, #0D1B2E 0%, #07111F 100%)", border: "1px solid var(--border-gold)", borderRadius: "var(--radius-xl)", padding: "48px" }}>
          <div className="grid-2" style={{ alignItems: "center" }}>
            <div>
              <div className="hero-kicker">EVERYDAY REINFORCEMENT</div>
              <h2 style={{ fontSize: "2.8rem", marginTop: "8px" }}>THE IDENTITY SHIFT™ DAILY PRACTICE KIT</h2>
              <p style={{ color: "var(--gold-warm)", fontWeight: 600, marginTop: "8px" }}>
                Your daily companion to the books and journals.
              </p>
              <p style={{ color: "var(--text-secondary)", margin: "16px 0 24px 0", lineHeight: 1.7 }}>
                The connective tissue between understanding and daily life. Designed to keep your transformation active through morning and evening rituals, focused affirmation decks, wall prints, and guided audio.
              </p>
              <div className="for-you-box" style={{ marginBottom: "28px" }}>
                <div className="for-you-title">WHAT THE KIT INCLUDES:</div>
                <ul className="for-you-list">
                  <li><strong>The 5-Step Daily System</strong> (Brain Primer, Neural Gratitude, Identity Scripting, Affirmations, Evening Rewire)</li>
                  <li><strong>48 Affirmation Cards</strong> (6 Decks: Wealth, Success, Health, Love, Self-Worth, Identity Practice)</li>
                  <li><strong>8 Wall Prints</strong> (Visual anchors for your environment)</li>
                  <li><strong>5 Guided Meditations</strong> (Audio reinforcement for rewiring)</li>
                </ul>
              </div>
              <div style={{ maxWidth: "260px" }}>
                <button type="button" className="look-inside-btn" onClick={() => setLookInsideBook("daily-practice-kit")}>
                  <span aria-hidden="true">👁</span> Look inside
                </button>
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <img src="/assets/daily-practice/daily-practice-kit.png" alt="Daily Practice Kit" width={1024} height={1536} loading="lazy" decoding="async" style={{ maxWidth: "320px", width: "100%", height: "auto", borderRadius: "var(--radius-md)", border: "1px solid var(--border-gold)", boxShadow: "0 15px 35px rgba(0,0,0,0.6)" }} />
            </div>
          </div>
        </div>
      </Reveal>

      {/* Who It's For — qualification checklist */}
      <Reveal as="section" className="container section-padding">
        <div style={{ background: "var(--navy-card)", border: "1px solid var(--border-gold)", borderRadius: "var(--radius-xl)", padding: "48px", maxWidth: "900px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "28px" }}>
            <div className="hero-kicker">QUALIFICATION</div>
            <h2 style={{ fontSize: "2.4rem" }}>WHO IS THE COMPLETE SYSTEM FOR?</h2>
          </div>
          <div className="for-you-box" style={{ padding: "28px" }}>
            <ul className="for-you-list" style={{ fontSize: "1rem", lineHeight: 1.8 }}>
              <li>You recognize the same patterns repeating in different areas of life (money, relationships, consistency).</li>
              <li>You want understanding and practical tools—not generic motivational hype.</li>
              <li>You want structured 30-day guided practice for daily behavior change.</li>
              <li>You want one connected journey instead of piecing together random self-help books.</li>
            </ul>
          </div>
          <div style={{ textAlign: "center", marginTop: "20px", fontWeight: 600, color: "var(--gold-warm)" }}>
            If more than one of these sounds like you, start with the complete connected journey.
          </div>
        </div>
      </Reveal>

      {/* System Integration — how the three layers fit together */}
      <Reveal as="section" className="container section-padding" style={{ borderTop: "1px solid var(--border-glass)" }}>
        <div style={{ textAlign: "center", maxWidth: "720px", margin: "0 auto" }}>
          <div className="hero-kicker">SYSTEM ARCHITECTURE</div>
          <h2 style={{ fontSize: "2.6rem" }}>WHY THESE PIECES BELONG TOGETHER</h2>
          <p style={{ color: "var(--text-secondary)", marginTop: "8px" }}>Three connected layers turning insight into everyday life.</p>
        </div>

        <div className="integration-grid">
          <div className="integration-card">
            <h4>01 — THE BOOKS GIVE YOU THE MAP</h4>
            <p>Understand the patterns, identity, and inherited influences shaping your choices.</p>
          </div>
          <div className="integration-card">
            <h4>02 — THE JOURNALS TURN INSIGHT INTO PRACTICE</h4>
            <p>Apply 30-day guided reflection to money, success, health, and relationships.</p>
          </div>
          <div className="integration-card">
            <h4>03 — THE DAILY PRACTICE KIT KEEPS THE WORK ALIVE</h4>
            <p>Create daily rituals, affirmation decks, and audio meditations that reinforce practice.</p>
          </div>
        </div>

        <div style={{ textAlign: "center", marginTop: "32px", fontFamily: "var(--font-serif)", fontSize: "1.5rem", color: "var(--gold-warm)" }}>
          ONE SYSTEM. THREE LAYERS. REAL-LIFE PRACTICE.
        </div>
      </Reveal>

      {/* Objection Handling — why the complete system vs. one book */}
      <Reveal as="section" className="container section-padding" style={{ maxWidth: "840px" }}>
        <div style={{ background: "var(--navy-card)", border: "1px solid var(--border-glass)", borderRadius: "var(--radius-lg)", padding: "36px" }}>
          <h3 className="font-serif" style={{ fontSize: "1.8rem", color: "var(--gold-warm)", marginBottom: "12px" }}>WHY THE COMPLETE SYSTEM INSTEAD OF ONE BOOK?</h3>
          <p style={{ color: "var(--text-secondary)", fontSize: "1.025rem", lineHeight: 1.7 }}>
            One book can help you explore one layer of your thinking. One journal can help you practice in one specific area. But human identity rarely lives in isolation—money stress affects health, and relationship patterns affect career follow-through. The Complete System connects all layers into one coherent journey.
          </p>
        </div>
      </Reveal>

      {/* Complete Collection Offer — pricing + CTA (#offer anchor target) */}
      <section id="offer" className="icc" aria-labelledby="icc-title">
        <div className="icc__wrap">

          {/* LEFT: what's inside */}
          <div>
            <h2 className="icc__title" id="icc-title">The Complete Collection</h2>
            <p className="icc__intro">The full journey from awareness to understanding to practice.</p>

            {/* Group 1: Books */}
            <div className="icc__group">
              <div className="icc__group-head">
                <h3 className="icc__group-name">The Trilogy</h3>
                <span className="icc__group-count">3 books, 525 pages</span>
              </div>
              <ul className="icc__list">
                <li className="icc__item">
                  <span className="icc__item-name">Book 1: The Pattern of You.</span>{" "}
                  <span className="icc__item-desc">See the patterns quietly running your life.</span>
                </li>
                <li className="icc__item">
                  <span className="icc__item-name">Book 2: The Probability of You.</span>{" "}
                  <span className="icc__item-desc">Expand what feels possible for you.</span>
                </li>
                <li className="icc__item">
                  <span className="icc__item-name">Book 3: The Inheritance of You.</span>{" "}
                  <span className="icc__item-desc">Understand what you were handed, and choose what you keep.</span>
                </li>
              </ul>
            </div>

            {/* Group 2: Journals */}
            <div className="icc__group">
              <div className="icc__group-head">
                <h3 className="icc__group-name">The Guided Journals</h3>
                <span className="icc__group-count">4 journals, 98 pages each</span>
              </div>
              <ul className="icc__list">
                <li className="icc__item">
                  <span className="icc__item-name">Wealth Journal.</span>{" "}
                  <span className="icc__item-desc">30 days to reshape your relationship with money.</span>
                </li>
                <li className="icc__item">
                  <span className="icc__item-name">Success Journal.</span>{" "}
                  <span className="icc__item-desc">30 days to align your actions with your goals.</span>
                </li>
                <li className="icc__item">
                  <span className="icc__item-name">Health Journal.</span>{" "}
                  <span className="icc__item-desc">30 days to build habits that hold.</span>
                </li>
                <li className="icc__item">
                  <span className="icc__item-name">Relationships Journal.</span>{" "}
                  <span className="icc__item-desc">30 days to deepen your connections.</span>
                </li>
              </ul>
            </div>

            {/* Group 3: Bonus */}
            <div className="icc__group icc__group--bonus">
              <div className="icc__group-head">
                <h3 className="icc__group-name">Daily Practice Kit</h3>
                <span className="icc__tag">Included bonus</span>
              </div>
              <ul className="icc__list">
                <li className="icc__item">
                  <span className="icc__item-desc">Everyday reinforcement so the work actually sticks.</span>
                </li>
              </ul>
            </div>
          </div>

          {/* RIGHT: price card */}
          <aside className="icc__card" aria-label="Pricing">
            <div className="icc__price"><sup>$</sup>47</div>

            <div className="icc__facts">
              <div>
                <span className="icc__fact-num">8</span>
                <span className="icc__fact-label">Products</span>
              </div>
              <div>
                <span className="icc__fact-num">900+</span>
                <span className="icc__fact-label">Pages</span>
              </div>
              <div>
                <span className="icc__fact-num">120</span>
                <span className="icc__fact-label">Days of practice</span>
              </div>
            </div>

            <p className="icc__per"><strong>Under $6 per product</strong><br />Less than 40 cents a day of practice</p>

            <Link className="icc__cta" to="/checkout?product=identity-shift-collection">Get the Complete Collection</Link>

            <p className="icc__delivery">Digital editions (PDF). Instant download after purchase. Read on any phone, tablet or computer.</p>
          </aside>

        </div>
      </section>

      {/* FAQ — frequently asked questions (FAQSection) */}
      <FAQSection />

      {/* "Look inside" modal — opened from the Trilogy BookCards */}
      {lookInsideBook ? (
        <BookLookInsideModal bookId={lookInsideBook} onClose={() => setLookInsideBook(null)} />
      ) : null}
    </Layout>
  );
}

const SUPPORT_EMAIL = "support@rajeshsivacharya.com";

const BOOKS = [
  {
    id: "pattern-of-you",
    num: "ONE",
    title: "The Pattern of You",
    subtitle:
      "Why Your Life Keeps Repeating: The Neuroscience of Breaking Subconscious Patterns, Rewiring Your Brain, and Creating a New Reality",
    desc: "Understand why the same patterns keep appearing in your life and how beliefs, attention, emotion, identity and repeated behavior can reinforce them.",
    img: "/assets/books/pattern-of-you.png",
  },
  {
    id: "probability-of-you",
    num: "TWO",
    title: "The Probability of You",
    subtitle:
      "Quantum Physics, Parallel Timelines, and the Science of Becoming Inevitable",
    desc: "Explore why your identity pulls you toward familiar choices, bridge state-of-being with physical action, and practice thinking from your future self.",
    img: "/assets/books/probability-of-you.png",
  },
  {
    id: "inheritance-of-you",
    num: "THREE",
    title: "The Inheritance of You",
    subtitle:
      "The Epigenetics of Generational Patterns: Break Family Cycles, Heal Inherited Trauma, End What Repeats With You, and Redesign Your Future",
    desc: "Explore family patterns and learned responses across biology, attachment, money, love, and worth—and decide what stops with you.",
    img: "/assets/books/inheritance-of-you.png",
  },
];

const JOURNALS = [
  {
    id: "wealth-journal",
    type: "wealth",
    title: "Wealth Journal",
    tagline: "CHANGE YOUR MONEY. CREATE FINANCIAL FREEDOM.",
    desc: "Money beliefs, financial identity, scarcity patterns, and building a wealth-oriented identity.",
    img: "/assets/journals/wealth.png",
  },
  {
    id: "success-journal",
    type: "success",
    title: "Success Journal",
    tagline: "ALIGN YOUR ACTIONS. ACHIEVE YOUR BIGGEST GOALS.",
    desc: "Success beliefs, professional identity, confidence, opportunity, ambition, and acting from a stronger future identity.",
    img: "/assets/journals/success.png",
  },
  {
    id: "health-journal",
    type: "health",
    title: "Health Journal",
    tagline: "REWIRE YOUR HABITS. TRANSFORM YOUR HEALTH.",
    desc: "Health-focused identity scripting, affirmations, body relationship work, and four phases culminating in embodiment.",
    img: "/assets/journals/health.png",
  },
  {
    id: "relationships-journal",
    type: "relationships",
    title: "Relationships Journal",
    tagline: "HEAL. FORGIVE. CONNECT. BUILD MEANINGFUL RELATIONSHIPS.",
    desc: "Attachment patterns, love beliefs, emotional connection, and developing a more secure relationship identity.",
    img: "/assets/journals/relationships.png",
  },
];

function FAQSection() {
  return (
    <section className="icf" id="faq" aria-labelledby="icf-title">
      <div className="icf__wrap">
        <h2 className="icf__title" id="icf-title">Frequently Asked Questions</h2>
        <p className="icf__intro">Everything you need to know before you begin.</p>

        <div className="icf__list">
          <details className="icf__item">
            <summary className="icf__q">What exactly do I get?<span className="icf__icon" aria-hidden="true" /></summary>
            <div className="icf__a">
              <p>The complete Identity Shift Collection:</p>
              <p><strong>3 books:</strong> The Pattern of You, The Probability of You, and The Inheritance of You (525 pages).</p>
              <p><strong>4 guided journals:</strong> Wealth, Success, Health, and Relationships. Each is a 30-day practice of 98 pages.</p>
              <p><strong>Daily Practice Kit:</strong> everyday reinforcement so the work actually sticks.</p>
              <p>That's 8 products and over 900 pages in total.</p>
            </div>
          </details>

          <details className="icf__item">
            <summary className="icf__q">Are these physical books?<span className="icf__icon" aria-hidden="true" /></summary>
            <div className="icf__a">
              <p>No. All eight products are digital editions (PDF), delivered instantly after purchase. You can read them on any phone, tablet, or computer, with nothing to wait for in the post.</p>
            </div>
          </details>

          <details className="icf__item">
            <summary className="icf__q">How do I receive my files after buying?<span className="icf__icon" aria-hidden="true" /></summary>
            <div className="icf__a">
              <p>You'll receive an email with your download links right after payment. If it doesn't arrive within a few minutes, please check your spam or promotions folder, or contact us at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.</p>
            </div>
          </details>

          <details className="icf__item">
            <summary className="icf__q">What is your refund policy?<span className="icf__icon" aria-hidden="true" /></summary>
            <div className="icf__a">
              <p>Please see our <Link to="/refund-policy">Refund Policy</Link> for full details, or email us at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we'll help.</p>
            </div>
          </details>

          <details className="icf__item">
            <summary className="icf__q">Why does my card statement show a different company name?<span className="icf__icon" aria-hidden="true" /></summary>
            <div className="icf__a">
              <p>Payments are processed securely by <strong>Dodo Payments</strong>, our authorized reseller, which handles payment processing and tax compliance. Their name will appear on your statement instead of ours.</p>
            </div>
          </details>

          <details className="icf__item">
            <summary className="icf__q">Are taxes included in the price?<span className="icf__icon" aria-hidden="true" /></summary>
            <div className="icf__a">
              <p>Yes. The price you see is the final price you pay, with all applicable taxes included.</p>
            </div>
          </details>

          <details className="icf__item">
            <summary className="icf__q">Do I need to read the books in order?<span className="icf__icon" aria-hidden="true" /></summary>
            <div className="icf__a">
              <p>We recommend it. The trilogy is designed as a journey: first recognize your patterns, then expand what feels possible, then understand what you've inherited. Each book builds on the one before.</p>
            </div>
          </details>

          <details className="icf__item">
            <summary className="icf__q">How do the journals work with the books?<span className="icf__icon" aria-hidden="true" /></summary>
            <div className="icf__a">
              <p>The books build understanding; the journals turn it into daily practice. Each journal guides you through 30 days in one area of life. You can start with whichever area matters most to you right now.</p>
            </div>
          </details>

          <details className="icf__item">
            <summary className="icf__q">Can I print the journals?<span className="icf__icon" aria-hidden="true" /></summary>
            <div className="icf__a">
              <p>Yes, for your own personal use. Many readers prefer writing by hand, and the journals are designed to print cleanly.</p>
            </div>
          </details>

          <details className="icf__item">
            <summary className="icf__q">Will this guarantee results?<span className="icf__icon" aria-hidden="true" /></summary>
            <div className="icf__a">
              <p>No book or journal can promise outcomes, and we won't pretend otherwise. These are reflective practices designed to support your growth, and what you get from them depends on how you apply them.</p>
              <p>The journals are not a substitute for financial, medical, or other professional advice.</p>
            </div>
          </details>

          <details className="icf__item">
            <summary className="icf__q">Is this based on a particular religion or belief system?<span className="icf__icon" aria-hidden="true" /></summary>
            <div className="icf__a">
              <p>No. This system is not based on any particular religion or religious belief system. It draws from concepts related to psychology, neuroscience, mindset, identity, habits, and personal development.</p>
              <p>You are free to interpret and apply the ideas according to your own beliefs, values, and worldview. No specific religious belief or spiritual practice is required.</p>
            </div>
          </details>

          <details className="icf__item">
            <summary className="icf__q">Can I buy the books or journals separately?<span className="icf__icon" aria-hidden="true" /></summary>
            <div className="icf__a">
              <p>Not at the moment. The collection is currently available only as a complete set.</p>
            </div>
          </details>
        </div>

        <div className="icf__contact">
          <strong>Still have a question?</strong>
          Email us at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. We reply within 24–48 hours.
        </div>
      </div>
    </section>
  );
}
function BookCard({ num, title, subtitle, desc, img, onLookInside }) {
  return (
    <div className="product-card">
      <div>
        <OptimizedImage src={img} alt={title} className="product-cover-img" width={280} height={420} />
        <div style={{ fontSize: "0.75rem", color: "var(--gold-warm)", fontWeight: 700 }}>BOOK {num}</div>
        <h3 style={{ fontSize: "1.4rem", margin: "4px 0" }}>{title}</h3>
        <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "12px" }}>{subtitle}</p>
        <p style={{ fontSize: "0.875rem", color: "var(--text-secondary)", lineHeight: 1.6 }}>{desc}</p>
      </div>
      <div style={{ marginTop: "20px", borderTop: "1px solid var(--border-glass)", paddingTop: "16px" }}>
        <button type="button" className="look-inside-btn" onClick={onLookInside}>
          <span aria-hidden="true">👁</span> Look inside
        </button>
      </div>
    </div>
  );
}
function JournalCard({ type, title, tagline, desc, img, onLookInside }) {
  return (
    <div className="product-card">
      <div>
        <span className={`journal-badge journal-${type}`}>30-DAY PRACTICE</span>
        <OptimizedImage src={img} alt={title} className="product-cover-img" width={280} height={420} />
        <h3 style={{ fontSize: "1.25rem", margin: "4px 0" }}>{title}</h3>
        <div style={{ fontSize: "0.8rem", fontWeight: 700, color: `var(--accent-${type})`, marginBottom: "8px" }}>{tagline}</div>
        <p style={{ fontSize: "0.825rem", color: "var(--text-muted)", marginBottom: "12px" }}>A 30-Day Neuroscience and Identity Practice</p>
        <p style={{ fontSize: "0.85rem", color: "var(--text-secondary)", lineHeight: 1.5 }}>{desc}</p>
      </div>
      <div style={{ marginTop: "20px", borderTop: "1px solid var(--border-glass)", paddingTop: "16px" }}>
        <button type="button" className="look-inside-btn" onClick={onLookInside}>
          <span aria-hidden="true">👁</span> Look inside
        </button>
      </div>
    </div>
  );
}

